import { useState } from 'react';
import { Button } from '@dataroom/shared';
import type { BillingInterval, CheckoutTier, TenantPlanTier } from '@dataroom/shared';
import { useBillingCheckout, useBillingPricing } from '@/api/hooks';
import { askCapLabel, billingErrorMessage, formatUsd, TIER_LABELS } from '@/lib/plan';
import { ArchivePackLine } from './ArchivePackLine';
import { Modal } from './Modal';

interface UpgradePlanModalProps {
  /** The tenant's current tier — its row is marked and not selectable. */
  currentTier?: TenantPlanTier;
  onClose: () => void;
}

const INTERVALS: { value: BillingInterval; label: string }[] = [
  { value: 'month', label: 'Month to month' },
  { value: 'year', label: '12 months, billed yearly' },
];

/**
 * Plan picker for "Upgrade" — shows each plan's monthly and 12-month price for
 * the firm's cohort (founding prices are kept while the firm stays subscribed),
 * what each includes (rooms, admin seats, the daily "Ask the Data Room" allowance),
 * the archive-pack line, lets the owner pick the interval, and starts a Stripe checkout (or moves a live
 * subscription to the plan) and redirects the browser to it. The prices come from
 * the server (`GET /tenant/billing/pricing`), and so do the limits; none is written here. While billing
 * is dormant (503) the friendly "Billing isn't set up yet." note shows instead.
 */
export function UpgradePlanModal({ currentTier, onClose }: UpgradePlanModalProps) {
  const checkout = useBillingCheckout();
  const pricing = useBillingPricing();
  const [pickedInterval, setPickedInterval] = useState<BillingInterval | null>(null);
  const data = pricing.data;
  const interval: BillingInterval = pickedInterval ?? data?.currentInterval ?? 'month';
  const pendingTier = checkout.isPending ? checkout.variables?.tier : undefined;
  const error = billingErrorMessage(checkout.error);

  function choose(tier: CheckoutTier) {
    checkout.mutate({ tier, interval });
  }

  return (
    <Modal
      title="Upgrade your plan"
      description="Pick a plan — you'll be taken to secure checkout."
      onClose={onClose}
      footer={
        <>
          {error ? <p className="text-sm text-destructive">{error}</p> : <span />}
          <Button variant="outline" size="sm" onClick={onClose} disabled={checkout.isPending}>
            Cancel
          </Button>
        </>
      }
    >
      {pricing.isPending ? (
        <p className="text-sm text-muted-foreground">Loading prices…</p>
      ) : pricing.error || !data ? (
        <p className="text-sm text-destructive">Prices could not be loaded. Try again shortly.</p>
      ) : (
        <div className="space-y-4">
          {data.cohort === 'founding' ? (
            <div className="rounded-md bg-primary/10 px-3 py-2 text-sm">
              <p className="font-medium">Founding price — kept while you stay subscribed</p>
              <p className="text-xs text-muted-foreground">
                {data.holdsFounding
                  ? 'Your founding price stays the same through renewals and plan changes. A lapse in your subscription ends it.'
                  : `${data.foundingSpotsLeft} of ${data.foundingSpotsTotal} founding spots left.`}
              </p>
            </div>
          ) : null}

          <div
            role="group"
            aria-label="Billing interval"
            className="grid grid-cols-2 gap-1 rounded-md bg-muted p-1"
          >
            {INTERVALS.map((i) => (
              <button
                key={i.value}
                type="button"
                aria-pressed={interval === i.value}
                onClick={() => setPickedInterval(i.value)}
                className={`rounded px-2 py-1.5 text-sm font-medium ${
                  interval === i.value ? 'bg-background shadow-sm' : 'text-muted-foreground'
                }`}
              >
                {i.label}
              </button>
            ))}
          </div>

          <ul className="space-y-3">
            {data.plans.map((prices) => {
              const tier = prices.tier;
              const current =
                tier === currentTier &&
                (data.currentInterval === null || data.currentInterval === interval);
              const perMonth = interval === 'year' ? prices.yearlyMonthlyCents : prices.monthlyCents;
              return (
                <li
                  key={tier}
                  className="flex items-center justify-between gap-3 rounded-md border p-3"
                >
                  <div>
                    <p className="font-medium">{TIER_LABELS[tier]}</p>
                    <p className="text-xs text-muted-foreground">
                      {prices.activeLimit} active {prices.activeLimit === 1 ? 'room' : 'rooms'} ·{' '}
                      {prices.archivedLimit} archived · {prices.seatLimit} admin{' '}
                      {prices.seatLimit === 1 ? 'seat' : 'seats'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Ask the Data Room: {askCapLabel(prices.askPerDay)}
                    </p>
                    <p className="mt-1 text-sm">
                      <span className="font-semibold">{formatUsd(perMonth)}</span>
                      <span className="text-muted-foreground"> / month</span>
                      {interval === 'year' ? (
                        <span className="text-xs text-muted-foreground">
                          {' '}
                          · {formatUsd(prices.yearlyCents)} billed yearly
                        </span>
                      ) : null}
                    </p>
                  </div>
                  {current ? (
                    <span className="text-xs font-medium text-muted-foreground">Current plan</span>
                  ) : (
                    <Button size="sm" onClick={() => choose(tier)} disabled={checkout.isPending}>
                      {pendingTier === tier ? 'Redirecting…' : 'Choose'}
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
          <ArchivePackLine className="text-sm text-muted-foreground" />
        </div>
      )}
    </Modal>
  );
}
